/**
 * Toast notification store
 * Manages toast notifications with auto-dismiss and stacking
 */

// Simple store implementation
let listeners = [];
let toasts = [];

function notify() {
  listeners.forEach((listener) => listener(toasts));
}

/**
 * @typedef {'success' | 'error' | 'warning' | 'info'} ToastType
 * @typedef {{ id: string, type: ToastType, message: string, title?: string, duration?: number }} ToastItem
 */

export const toastStore = {
  /**
   * Subscribe to toast changes
   * @param {(toasts: ToastItem[]) => void} listener
   * @returns {() => void} Unsubscribe function
   */
  subscribe(listener) {
    listeners.push(listener);
    listener(toasts);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  },

  /**
   * Add a toast notification
   * @param {Omit<ToastItem, 'id'>} toast
   * @returns {string} Toast ID
   */
  add(toast) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = {
      id,
      duration: 3000, // Default 3 seconds
      ...toast,
    };

    toasts = [...toasts, newToast];

    // Limit to 5 toasts max, remove oldest
    if (toasts.length > 5) {
      toasts = toasts.slice(-5);
    }

    notify();
    return id;
  },

  /**
   * Dismiss a toast by ID
   * @param {string} id
   */
  dismiss(id) {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  },

  /**
   * Clear all toasts
   */
  clear() {
    toasts = [];
    notify();
  },

  // Convenience methods
  /**
   * Show a success toast
   * @param {string} message
   * @param {string} [title]
   * @param {number} [duration]
   */
  success(message, title, duration) {
    return this.add({ type: "success", message, title, duration });
  },

  /**
   * Show an error toast
   * @param {string} message
   * @param {string} [title]
   * @param {number} [duration]
   */
  error(message, title, duration = 4000) {
    return this.add({ type: "error", message, title, duration });
  },

  /**
   * Show a warning toast
   * @param {string} message
   * @param {string} [title]
   * @param {number} [duration]
   */
  warning(message, title, duration) {
    return this.add({ type: "warning", message, title, duration });
  },

  /**
   * Show an info toast
   * @param {string} message
   * @param {string} [title]
   * @param {number} [duration]
   */
  info(message, title, duration) {
    return this.add({ type: "info", message, title, duration });
  },
};

// Default export for convenience
export default toastStore;
